import { useState } from 'react';

interface OnboardingStep4Props {
  onComplete: (data: {
    chronicConditions: string[];
    nutrition: {
      fruitsVeggiesDaily: number;
      whiteMeatWeekly: number;
      fishWeekly: number;
      fiberIntake: 'low' | 'moderate' | 'high';
      processedFoodFrequency: 'rarely' | 'sometimes' | 'often' | 'daily';
      wholeFoodsPreference: 'rarely' | 'sometimes' | 'mostly' | 'always';
      waterIntake: number;
    };
  }) => void;
  onBack: () => void;
}

export default function OnboardingStep4({ onComplete, onBack }: OnboardingStep4Props) {
  const [chronicConditions, setChronicConditions] = useState<string[]>([]);
  const [customCondition, setCustomCondition] = useState('');
  
  // Nutrition states
  const [fruitsVeggiesDaily, setFruitsVeggiesDaily] = useState(3);
  const [whiteMeatWeekly, setWhiteMeatWeekly] = useState(2);
  const [fishWeekly, setFishWeekly] = useState(1);
  const [fiberIntake, setFiberIntake] = useState<'low' | 'moderate' | 'high'>('moderate');
  const [processedFoodFrequency, setProcessedFoodFrequency] = useState<'rarely' | 'sometimes' | 'often' | 'daily'>('sometimes');
  const [wholeFoodsPreference, setWholeFoodsPreference] = useState<'rarely' | 'sometimes' | 'mostly' | 'always'>('sometimes');
  const [waterIntake, setWaterIntake] = useState(6);

  const commonConditions = [
    'Hypertension',
    'Type 2 Diabetes',
    'Type 1 Diabetes',
    'Heart Disease',
    'Asthma',
    'COPD',
    'Arthritis',
    'Depression',
    'Anxiety',
    'Thyroid Disorder',
    'High Cholesterol',
    'Other'
  ];

  const toggleCondition = (condition: string) => {
    if (chronicConditions.includes(condition)) {
      setChronicConditions(chronicConditions.filter(c => c !== condition));
    } else {
      setChronicConditions([...chronicConditions, condition]);
    }
  };

  const addCustomCondition = () => {
    if (customCondition.trim() && !chronicConditions.includes(customCondition.trim())) {
      setChronicConditions([...chronicConditions, customCondition.trim()]);
      setCustomCondition('');
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onComplete({
      chronicConditions,
      nutrition: {
        fruitsVeggiesDaily,
        whiteMeatWeekly,
        fishWeekly,
        fiberIntake,
        processedFoodFrequency,
        wholeFoodsPreference,
        waterIntake
      }
    });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-8">
      {/* Nutrition Section */}
      <div>
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
            <i className="ri-restaurant-2-line text-xl text-green-600"></i>
          </div>
          <div>
            <h3 className="text-xl font-bold text-gray-900">Eating Habits</h3>
            <p className="text-sm text-gray-600">Tell us about your daily nutrition</p>
          </div>
        </div>

        {/* Fruits & Vegetables */}
        <div className="mb-6">
          <label className="block text-sm font-semibold text-gray-900 mb-3">
            Fruits & Vegetables Servings per Day
          </label>
          <div className="flex items-center gap-4">
            <input
              type="range"
              min="0"
              max="10"
              value={fruitsVeggiesDaily}
              onChange={(e) => setFruitsVeggiesDaily(parseInt(e.target.value))}
              className="flex-1"
            />
            <span className="w-16 text-center px-3 py-2 bg-green-50 text-green-700 rounded-lg font-semibold">
              {fruitsVeggiesDaily}
            </span>
          </div>
          <p className="text-xs text-gray-600 mt-2">Recommended: 5+ servings daily</p>
        </div>

        {/* White Meat */}
        <div className="mb-6">
          <label className="block text-sm font-semibold text-gray-900 mb-3">
            White Meat (chicken, turkey) per Week
          </label>
          <div className="flex items-center gap-4">
            <input
              type="range"
              min="0"
              max="7"
              value={whiteMeatWeekly}
              onChange={(e) => setWhiteMeatWeekly(parseInt(e.target.value))}
              className="flex-1"
            />
            <span className="w-16 text-center px-3 py-2 bg-amber-50 text-amber-700 rounded-lg font-semibold">
              {whiteMeatWeekly}
            </span>
          </div>
          <p className="text-xs text-gray-600 mt-2">Times per week</p>
        </div>

        {/* Fish */}
        <div className="mb-6">
          <label className="block text-sm font-semibold text-gray-900 mb-3">
            Fish & Seafood per Week
          </label>
          <div className="flex items-center gap-4">
            <input
              type="range"
              min="0"
              max="7"
              value={fishWeekly}
              onChange={(e) => setFishWeekly(parseInt(e.target.value))}
              className="flex-1"
            />
            <span className="w-16 text-center px-3 py-2 bg-blue-50 text-blue-700 rounded-lg font-semibold">
              {fishWeekly}
            </span>
          </div>
          <p className="text-xs text-gray-600 mt-2">Recommended: 2+ times weekly</p>
        </div>

        {/* Fiber Intake */}
        <div className="mb-6">
          <label className="block text-sm font-semibold text-gray-900 mb-3">
            Fiber Intake (whole grains, beans, legumes)
          </label>
          <div className="grid grid-cols-3 gap-3">
            {['low', 'moderate', 'high'].map((level) => (
              <button
                key={level}
                type="button"
                onClick={() => setFiberIntake(level as any)}
                className={`px-4 py-3 rounded-lg border-2 transition-all capitalize whitespace-nowrap cursor-pointer ${
                  fiberIntake === level
                    ? 'border-teal-500 bg-teal-50 text-teal-700 font-semibold'
                    : 'border-gray-200 hover:border-gray-300 text-gray-700'
                }`}
              >
                {level}
              </button>
            ))}
          </div>
        </div>

        {/* Processed Food Frequency */}
        <div className="mb-6">
          <label className="block text-sm font-semibold text-gray-900 mb-3">
            Processed Food Frequency
          </label>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {['rarely', 'sometimes', 'often', 'daily'].map((freq) => (
              <button
                key={freq}
                type="button"
                onClick={() => setProcessedFoodFrequency(freq as any)}
                className={`px-4 py-3 rounded-lg border-2 transition-all capitalize whitespace-nowrap cursor-pointer ${
                  processedFoodFrequency === freq
                    ? 'border-teal-500 bg-teal-50 text-teal-700 font-semibold'
                    : 'border-gray-200 hover:border-gray-300 text-gray-700'
                }`}
              >
                {freq}
              </button>
            ))}
          </div>
        </div>

        {/* Whole Foods Preference */}
        <div className="mb-6">
          <label className="block text-sm font-semibold text-gray-900 mb-3">
            How Often Do You Choose Whole Foods?
          </label>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {['rarely', 'sometimes', 'mostly', 'always'].map((pref) => (
              <button
                key={pref}
                type="button"
                onClick={() => setWholeFoodsPreference(pref as any)}
                className={`px-4 py-3 rounded-lg border-2 transition-all capitalize whitespace-nowrap cursor-pointer ${
                  wholeFoodsPreference === pref
                    ? 'border-teal-500 bg-teal-50 text-teal-700 font-semibold'
                    : 'border-gray-200 hover:border-gray-300 text-gray-700'
                }`}
              >
                {pref}
              </button>
            ))}
          </div>
        </div>

        {/* Water Intake */}
        <div className="mb-6">
          <label className="block text-sm font-semibold text-gray-900 mb-3">
            Water Intake (glasses per day)
          </label>
          <div className="flex items-center gap-4">
            <input
              type="range"
              min="0"
              max="15"
              value={waterIntake}
              onChange={(e) => setWaterIntake(parseInt(e.target.value))}
              className="flex-1"
            />
            <span className="w-16 text-center px-3 py-2 bg-blue-50 text-blue-700 rounded-lg font-semibold">
              {waterIntake}
            </span>
          </div>
          <p className="text-xs text-gray-600 mt-2">Recommended: 8 glasses daily</p>
        </div>
      </div>

      {/* Divider */}
      <div className="border-t-2 border-gray-200"></div>

      {/* Medical Conditions */}
      <div>
        <label className="block text-sm font-semibold text-gray-900 mb-3">
          Do you have any of these conditions?
        </label>
        <p className="text-xs text-gray-600 mb-3">Select all that apply</p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {commonConditions.map((condition) => (
            <label
              key={condition}
              className="flex items-center gap-3 p-3 border-2 border-gray-200 rounded-lg hover:border-teal-300 cursor-pointer transition-colors"
            >
              <input
                type="checkbox"
                checked={chronicConditions.includes(condition)}
                onChange={(e) => {
                  if (e.target.checked) {
                    setChronicConditions([...chronicConditions, condition]);
                  } else {
                    setChronicConditions(chronicConditions.filter(c => c !== condition));
                  }
                }}
                className="w-5 h-5 text-teal-600 border-gray-300 rounded focus:ring-teal-500 cursor-pointer"
              />
              <span className="text-sm text-gray-900">{condition}</span>
            </label>
          ))}
        </div>
      </div>

      {/* Navigation Buttons */}
      <div className="flex justify-between pt-6">
        <button
          type="button"
          onClick={onBack}
          className="px-8 py-3 border-2 border-gray-300 text-gray-700 rounded-lg font-semibold hover:border-gray-400 transition-colors whitespace-nowrap cursor-pointer"
        >
          Back
        </button>
        <button
          type="submit"
          className="px-8 py-3 bg-teal-600 text-white rounded-lg font-semibold hover:bg-teal-700 transition-colors whitespace-nowrap cursor-pointer"
        >
          Calculate My Score
        </button>
      </div>
    </form>
  );
}
